// ✅ File: src/pages/VehicleV2/FileManagerInvoiceDoc.jsx
// React version of: FileManagerInvoicedoc.aspx
// ✅ NOW uses docId (NOT invoiceVehicleId)
// ✅ Uses localStorage-based backend headers
// - Rights check
// - List invoice documents by docId
// - Show document opens in new tab
// - Remove deletes selected documents

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FaArrowLeft, FaTrash, FaEye } from "react-icons/fa";

// ✅ Backend base
const API_BASE = "http://nissi-india.com/api/fileManagerInvoiceDoc";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v) => String(v ?? "").trim();

export default function FileManagerInvoiceDoc() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();

  const { skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ docId can come from:
  // 1) navigate("/vehicle/FileManagerInvoiceDocs", { state: { docId: 123 }})
  // 2) /vehicle/FileManagerInvoiceDocs?docId=123
  // 3) sessionStorage.setItem("InvoiceDocId","123")  (optional fallback)
  const docId = useMemo(() => {
    const fromState = toInt(state?.docId, 0);
    if (fromState) return fromState;

    const fromQS = toInt(searchParams.get("docId"), 0);
    if (fromQS) return fromQS;

    const fromSession = toInt(sessionStorage.getItem("InvoiceDocId"), 0);
    if (fromSession) return fromSession;

    return 0;
  }, [state, searchParams]);

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ backend matching headers
  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({}); // { [fld_invoicedocumentid]: true }

  /* ---------------- rights check ---------------- */
  const checkRights = async () => {
    try {
      // backend should return: { success:true, rights:true/false }
      const { data } = await axios.get(`${API_BASE}/rights`, {
        params: { userId: toInt(userId, 0) },
        headers: apiHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Rights check failed");
        navigate("/vehicle/VehicleHome");
        return false;
      }

      if (!data?.rights) {
        toast.error("You dont have rights to access this page");
        navigate("/vehicle/VehicleHome");
        return false;
      }

      return true;
    } catch (err) {
      console.error("Rights check error:", err);
      toast.error(err?.response?.data?.message || "You dont have rights to access this page");
      navigate("/vehicle/VehicleHome");
      return false;
    }
  };

  /* ---------------- list docs ---------------- */
  const fetchDocs = async () => {
    if (!docId) {
      setRows([]);
      setSelected({});
      toast.info("Document ID not found");
      return;
    }

    setLoading(true);
    try {
      // backend should return:
      // { success:true, rows:[ { fld_invoicedocumentid, fld_document }, ... ] }
      const { data } = await axios.get(`${API_BASE}/list`, {
        params: { docId },
        headers: apiHeaders,
      });

      if (data?.success === false) {
        setRows([]);
        setSelected({});
        toast.info(data?.message || "No Records Found");
        return;
      }

      const list = Array.isArray(data?.rows) ? data.rows : [];
      setRows(list);
      setSelected({});
      if (!list.length) toast.info("No Records Found");
    } catch (err) {
      console.error("Fetch docs error:", err);
      setRows([]);
      setSelected({});
      toast.error(err?.response?.data?.message || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- remove selected ---------------- */
  const removeSelected = async () => {
    const ids = Object.keys(selected)
      .filter((k) => selected[k])
      .map((k) => toInt(k, 0))
      .filter((n) => n > 0);

    if (!ids.length) {
      toast.info("Select at least one document to remove");
      return;
    }

    const ok = window.confirm("Are you sure you want to delete selected documents?");
    if (!ok) return;

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/remove`,
        {
          ids,
          docId,
        },
        {
          headers: apiHeaders,
        }
      );

      if (data?.success === false) {
        toast.error(data?.message || "Document cannot be Removed.");
        return;
      }

      toast.success(data?.message || "Deleted Successfully");
      await fetchDocs();
    } catch (err) {
      console.error("Remove docs error:", err);
      toast.error(err?.response?.data?.message || "Document cannot be Removed.");
    } finally {
      setLoading(false);
    }
  };

  const toggleOne = (id, checked) => {
    setSelected((prev) => ({ ...prev, [id]: checked }));
  };

  const toggleAll = (checked) => {
    const next = {};
    rows.forEach((r) => {
      next[r.fld_invoicedocumentid] = checked;
    });
    setSelected(next);
  };

  const openDoc = (filename) => {
    const safeName = toStr(filename);
    if (!safeName) return;

    // ✅ Same as ASPX:
    // window.open("../VehicleV2/InvoiceDocument/" + filename)
    const url = `/VehicleV2/InvoiceDocument/${encodeURIComponent(safeName)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    (async () => {
      const ok = await checkRights();
      if (!ok) return;
      await fetchDocs();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, apiHeaders]);

  const anySelected = useMemo(() => Object.values(selected).some(Boolean), [selected]);

  const allSelected =
    rows.length > 0 && rows.every((r) => !!selected[r.fld_invoicedocumentid]);

  return (
    <VehicleLayoutWrapper>
      <HeaderBar />

      <div className="p-4">
        <ToastContainer />

        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className={`${buttonClass} ${hoverClass} text-white px-3 py-2 rounded flex items-center gap-2`}
            >
              <FaArrowLeft /> Back
            </button>

            <h2 className="text-lg font-semibold">File Manager Invoice Document</h2>
          </div>

          <button
            onClick={removeSelected}
            disabled={loading || !anySelected}
            className={`${
              loading || !anySelected ? "bg-gray-400 cursor-not-allowed" : `${buttonClass} ${hoverClass}`
            } text-white px-3 py-2 rounded flex items-center gap-2`}
            title="Remove selected"
          >
            <FaTrash /> Remove
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded shadow p-3 overflow-auto">
          <table className="min-w-[900px] w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left p-2 whitespace-nowrap">S.No</th>
                <th className="text-left p-2 whitespace-nowrap">Document</th>
                <th className="text-left p-2 whitespace-nowrap">View</th>
                <th className="text-left p-2 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    Select
                    <input
                      type="checkbox"
                      onChange={(e) => toggleAll(e.target.checked)}
                      checked={allSelected}
                    />
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td className="p-3" colSpan={4}>
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td className="p-3 text-center" colSpan={4}>
                    No Records Found
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={r.fld_invoicedocumentid || idx} className="border-t">
                    <td className="p-2 whitespace-nowrap">{idx + 1}</td>

                    <td className="p-2 whitespace-nowrap">{r.fld_document}</td>

                    <td className="p-2 whitespace-nowrap">
                      <button
                        className={`${buttonClass} ${hoverClass} text-white px-3 py-1 rounded inline-flex items-center gap-2`}
                        onClick={() => openDoc(r.fld_document)}
                      >
                        <FaEye /> Show
                      </button>
                    </td>

                    <td className="p-2 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={!!selected[r.fld_invoicedocumentid]}
                        onChange={(e) =>
                          toggleOne(r.fld_invoicedocumentid, e.target.checked)
                        }
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Small note */}
        <div className="text-xs text-gray-500 mt-2 space-y-1">
          <div>
            Doc ID: <b>{docId || "-"}</b>
          </div>
          <div>
            Branch ID: <b>{branchId || "-"}</b> | Branch Type: <b>{branchType || "-"}</b>
          </div>
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}